import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import { dealerSelectors } from '@makemydeal/dr-shared-store';

import { changeVehicleCreators, changeVehicleSelectors } from '../../store';
import { changeVehicleConfig } from '../../store/config';
import { isDealVehicleInStock, isSameVehicle, toValidatableVehicle } from '../../utils/dealVehicle';
import { isValidVehicle } from '../../utils/isValidVehicle';
import type { CatalogVehicle } from '../../types';
import { useVehicleSearchFiltersContext } from './VehicleSearchFiltersContext';

export const useAutoExpandDealVehicle = (searchResults: CatalogVehicle[] | undefined, isSearchTabActive: boolean) => {
    const dispatch = useDispatch();
    const isEnabled = useSelector(changeVehicleSelectors.isAutoExpandDealVehicleEnabled);
    const dealVehicle = useSelector(changeVehicleSelectors.getDealVehicle);
    const isDealerAISearchEnabled = useSelector(dealerSelectors.isDealerAISearchEnabled);
    const isFetching = useSelector(changeVehicleSelectors.getIsFetching);
    const selectedVehicle = useSelector(changeVehicleSelectors.getSelectedVehicle);
    const isSettled = useSelector(changeVehicleSelectors.getIsAutoExpandSettled);
    const { hasUserInteracted } = useVehicleSearchFiltersContext();

    const hasSeenFetch = useRef(false);
    const hasLogged = useRef(false);

    useEffect(() => {
        if (isFetching) {
            hasSeenFetch.current = true;
        }
    }, [isFetching]);

    useEffect(() => {
        if (isEnabled && (hasUserInteracted || !isSearchTabActive)) {
            dispatch(changeVehicleCreators.autoExpandSettled());
        }
    }, [isEnabled, hasUserInteracted, isSearchTabActive, dispatch]);

    useEffect(() => {
        if (!isEnabled || isDealerAISearchEnabled || hasLogged.current || isFetching || !hasSeenFetch.current || !searchResults) {
            return;
        }

        hasLogged.current = true;
        const isPriced = !!dealVehicle && isValidVehicle(toValidatableVehicle(dealVehicle));
        logNewRelicPageAction('changeVehicleOpened', {
            host: changeVehicleConfig.hostApp ?? 'unknown',
            dealVehicleState: isPriced ? 'priced' : 'unpriced',
            autoExpandFired: !isSettled && isDealVehicleInStock(dealVehicle) && isSameVehicle(selectedVehicle, dealVehicle)
        });
    }, [isEnabled, isDealerAISearchEnabled, isFetching, searchResults, dealVehicle, selectedVehicle, isSettled]);
};
